import React from "react";
import {
  Modal,
  View,
  StyleSheet,
  ScrollView,
  useWindowDimensions,
  type ModalProps,
  type StyleProp,
  type ViewStyle,
} from "react-native";
import CustomButton from "../CustomButton/CustomButton";
import CustomText from "../CustomText/CustomText";
import { ModalExit } from "../../../svgIcons";
import { shadowStyles } from "../../../../styles/shadow";
import { PaperProvider } from "react-native-paper";
import { getModalAnimationType } from "../../../../utils/getModalAnimationType";

interface CustomModalProps extends Omit<ModalProps, "visible" | "onRequestClose"> {
  visible: boolean;
  onRequestClose: () => void;
  children: React.ReactNode;
  style?: StyleProp<ViewStyle>;
  bodyStyle?: StyleProp<ViewStyle>;
  showCloseButton?: boolean;
  title?: string;
  exitFill?: string;
  onShow?: ModalProps["onShow"];
  ariaLabel?: string;
  translateTitle?: boolean;
}

const CustomModal: React.FC<CustomModalProps> = ({
  visible,
  onRequestClose,
  children,
  style = {},
  bodyStyle = {},
  showCloseButton = false,
  title = "",
  exitFill = "#000000",
  onShow = undefined,
  ariaLabel = "",
  translateTitle = true,
  animationType,
  ...props
}) => {
  const { width: windowWidth, height: windowHeight } = useWindowDimensions();

  // Keep the modal usable on small screens:
  // - Always leave gutters so it never touches screen edges
  // - Constrain height so header stays reachable
  const horizontalGutter = 16;
  const verticalGutter = 24;
  const maxModalWidth = Math.max(0, windowWidth - horizontalGutter * 2);
  const maxModalHeight = Math.max(0, windowHeight - verticalGutter * 2);

  const resolvedAriaLabel = ariaLabel || title;

  return (
    <Modal
      role="dialog"
      aria-labelledby={resolvedAriaLabel}
      id="custom-modal"
      testID="activity-modal"
      animationType={getModalAnimationType(animationType ?? "fade")}
      transparent={true}
      visible={visible}
      onRequestClose={onRequestClose}
      onShow={onShow}
      style={{ overflow: "hidden", height: "100%" }}
      {...props}>
      <PaperProvider>
        <View style={styles.centeredView}>
          <View
            style={[
              styles.modalView,
              shadowStyles.modal,
              style,
              // Enforce viewport constraints last so callers can't override them.
              {
                maxWidth: Math.min(900, maxModalWidth),
                maxHeight: maxModalHeight,
              },
            ]}>
            {(title || showCloseButton) && (
              <View style={styles.header}>
                <CustomText
                  role="heading"
                  aria-level={1}
                  text={title}
                  size="s"
                  textType="display"
                  weight="ultraBold"
                  useTranslationText={false}
                />
                {showCloseButton && (
                  <CustomButton
                    styleType="transparent"
                    leftIcon={<ModalExit fill={exitFill} />}
                    onPress={onRequestClose}
                    aria-label="Close modal"
                  />
                )}
              </View>
            )}
            <ScrollView
              style={[styles.body, bodyStyle]}
              keyboardShouldPersistTaps="handled"
              contentContainerStyle={{ flexGrow: 1, minHeight: 0, overflow: "hidden", height: "100%" }}
              showsVerticalScrollIndicator={false}
              tabIndex={0}>
              {children}
            </ScrollView>
          </View>
        </View>
      </PaperProvider>
    </Modal>
  );
};

const styles = StyleSheet.create({
  centeredView: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    paddingHorizontal: 16,
    paddingVertical: 24,
  },
  modalView: {
    borderRadius: 20,
    padding: 20,
    width: "100%",
    overflow: "visible",
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 5,
  },
  body: {
    flexShrink: 1,
    flexGrow: 1,
    marginTop: 8,
  },
});

export default CustomModal;
